import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FormEvent, useMemo, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import {
  useAuthUserRestaurant,
  useCreateProductOfferMutation,
  ValidationError,
} from '@durma-soft/gros-sdk';

import { Modal } from '@/components/shared/modal';
import { Button } from '@/components/shared/shadcn-ui/button';
import { DialogFooter } from '@/components/shared/shadcn-ui/dialog';
import {
  ProductOfferFormData,
  ProductOfferFormFields,
} from '@/components/restaurant/product-offers/product-offer-form-fields';

const initialFormData: ProductOfferFormData = {
  name: '',
  supplier_id: null,
  price_per_unit: null,
  unit: '',
  currency: 'BAM',
  image: null,
};

export const CreateProductOfferModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const closeModal = () =>
    navigate('/restaurant/platform/product-offers', { replace: true });

  const isModalOpen = Boolean(
    useMatch({ path: '/restaurant/platform/product-offers/create' }),
  );

  const [formData, setFormData] =
    useState<ProductOfferFormData>(initialFormData);

  const restaurant = useAuthUserRestaurant();
  const createProductOffer = useCreateProductOfferMutation();

  const isValid = useMemo(() => {
    return (
      formData.name.length > 0 &&
      formData.supplier_id != null &&
      formData.price_per_unit != null &&
      formData.price_per_unit > 0 &&
      formData.unit.length > 0
    );
  }, [formData]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) return;
    try {
      await createProductOffer.mutateAsync({
        restaurant_id: restaurant.id,
        name: formData.name,
        supplier_id: formData.supplier_id!,
        price_per_unit: formData.price_per_unit!,
        unit: formData.unit,
        currency: formData.currency,
        image: typeof formData.image === 'string' ? undefined : formData.image,
      });
      closeModal();
      setFormData(initialFormData);
      toast.success(t('product-offers.create-success-msg'));
    } catch (error) {
      if (error instanceof ValidationError) {
        return toast.error(error.message);
      }
      toast.error(t('product-offers.create-error-msg'));
    }
  };

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      title={t('product-offers.create')}
      description={t('product-offers.create-details')}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <ProductOfferFormFields formData={formData} setFormData={setFormData} />
        <DialogFooter className="mt-2">
          <Button
            type="submit"
            disabled={!isValid}
            isLoading={createProductOffer.isPending}
          >
            {t('product-offers.create')}
          </Button>
        </DialogFooter>
      </form>
    </Modal>
  );
};
